import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import { isVerifyingCreditCard, isAddingCreditCard } from '../../reducers/checkoutPaymentReducer';
import { PAYMENTS } from '../../constants/checkoutConstants';

// components
import CheckoutCreditCard from './CheckoutCreditCard';

import { toggleCreditCardListExpanded } from '../../actions/checkoutPaymentActions';

import styles from './styles/CheckoutCreditCardList.scss';

function CheckoutCreditCardList({
    viewer,
    buyer,
    isAdding,
    isVerifying,
    verifyCardToken,
    lastValidCreditCardToken,
    paymentMethodToken,
    actions,
    onSelect,
    checkoutPaymentErrors,
    onValidationUpdate,
    onValidationError,
    deselectCreditCards,
    expandableCardList = false,
    isCreditCardListExpanded,
    onToggleCreditCardListExpanded,
}) {
    const walletPaymentMethods = get(buyer, 'wallet.paymentMethods') || [];
    const creditCards = walletPaymentMethods.filter(
        ({ paymentMethod }) =>
            paymentMethod && paymentMethod.__typename === PAYMENTS.TYPE_CREDIT_CARD
    );
    // If list is expandable show only first card.
    const creditCardsToShow = expandableCardList ? creditCards.slice(0, 1) : creditCards;
    // Rest of the cards can be hidden if list is expandable.
    const expandableCreditCards =
        expandableCardList && isCreditCardListExpanded ? creditCards.slice(1) : [];

    const getCheckoutCreditCard = creditCard => {
        const cardToken = get(creditCard, 'paymentMethod.token');
        const cardProps = {
            viewer,
            buyer,
            actions,
            onSelect,
            onValidationUpdate,
            onValidationError,
            checkoutPaymentErrors,
        };
        const isSelected =
            ((cardToken === (lastValidCreditCardToken || paymentMethodToken) &&
                !isAdding &&
                !isVerifying) ||
                (cardToken === verifyCardToken && isVerifying)) &&
            !deselectCreditCards;

        return (
            <CheckoutCreditCard
                key={cardToken}
                {...cardProps}
                isSelected={isSelected}
                cardToken={cardToken}
            />
        );
    };

    return (
        <div data-tn="credit-cards-container" className="fs-block">
            {creditCardsToShow.map(creditCard => getCheckoutCreditCard(creditCard))}
            {expandableCreditCards.map(expandableCreditCard =>
                getCheckoutCreditCard(expandableCreditCard)
            )}
            {expandableCardList && creditCards.length > 1 && (
                <div
                    className={styles.expandLink}
                    onClick={onToggleCreditCardListExpanded}
                    onKeyDown={onToggleCreditCardListExpanded}
                    role="link"
                    tabIndex={0}
                >
                    {isCreditCardListExpanded ? (
                        <FormattedMessage
                            id="checkout.CheckoutCreditCardList.hideList"
                            defaultMessage="Hide saved cards"
                        />
                    ) : (
                        <FormattedMessage
                            id="checkout.CheckoutCreditCardList.showList"
                            defaultMessage="View all saved cards"
                        />
                    )}
                </div>
            )}
        </div>
    );
}

CheckoutCreditCardList.propTypes = {
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    isAdding: PropTypes.bool.isRequired,
    isVerifying: PropTypes.bool.isRequired,
    verifyCardToken: PropTypes.string,
    lastValidCreditCardToken: PropTypes.string,
    paymentMethodToken: PropTypes.string,
    actions: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    checkoutPaymentErrors: PropTypes.object.isRequired,
    onValidationUpdate: PropTypes.func.isRequired,
    onValidationError: PropTypes.func.isRequired,
    deselectCreditCards: PropTypes.bool.isRequired,
    expandableCardList: PropTypes.bool,
    isCreditCardListExpanded: PropTypes.bool.isRequired,
    onToggleCreditCardListExpanded: PropTypes.func.isRequired,
};

const ConnectedCheckoutCreditCardList = connect(
    ({ transaction, payment }) => ({
        paymentMethodToken: transaction.paymentMethodToken,
        verifyCardToken: payment.verifyCardToken,
        lastValidCreditCardToken: payment.lastValidCreditCardToken,
        isVerifying: isVerifyingCreditCard(payment),
        isAdding: isAddingCreditCard(payment),
        deselectCreditCards: payment.deselectCreditCards,
        isCreditCardListExpanded: payment.isCreditCardListExpanded,
    }),
    {
        onToggleCreditCardListExpanded: toggleCreditCardListExpanded,
    }
)(CheckoutCreditCardList);

export default createFragmentContainer(ConnectedCheckoutCreditCardList, {
    viewer: graphql`
        fragment CheckoutCreditCardList_viewer on Viewer {
            ...CheckoutCreditCard_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutCreditCardList_buyer on User {
            ...CheckoutCreditCard_buyer
            wallet {
                paymentMethods {
                    paymentMethod {
                        __typename
                        ... on CreditCard {
                            token
                        }
                    }
                }
            }
        }
    `,
});
